import { useMemo, useState } from 'react';
import { Input, Label } from '@artstorefronts/ui';
import { buildWizardLink } from './draft';
import { CopyButton } from './CopyButton';

/**
 * A scratch pad, not stored state. A wizard owns no page, so the thing a campaign needs is a
 * finished URL: paste whatever link the campaign is already sending, get it back with the
 * parameter appended. Nothing typed here is saved with the wizard.
 */
export function LinkBuilder({ slug }: { slug: string }) {
  const [url, setUrl] = useState('');
  const built = useMemo(() => buildWizardLink(url, slug), [url, slug]);

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor="wizard-link-builder" className="text-sm font-medium">
          Try it against a URL
        </Label>
        <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600">not saved</span>
      </div>
      <Input
        id="wizard-link-builder"
        type="text"
        className="mt-2 w-full font-mono text-sm"
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        placeholder="https://www.arthelper.com/community/mattmillsart"
      />
      <div className="mt-2 flex items-start gap-2">
        <code className="min-w-0 flex-1 break-all rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-800">
          {built || 'Paste a URL above to build the finished link.'}
        </code>
        <CopyButton value={built} ariaLabel="Copy the finished link" />
      </div>
      <p className="mt-2 text-xs text-gray-500">
        {slug
          ? 'Nothing here is saved — it is a scratch pad for building one finished link to paste into a campaign, or a different one per recipient.'
          : 'Set a slug above first; the parameter is built from it.'}
      </p>
    </div>
  );
}
